import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { array, func, object, bool, number } from 'prop-types';
import SlideoutError from 'ps-components/Slideout/Error/SlideoutError';
import SlideoutSubmitSuccess from 'ps-components/Slideout/SubmitSuccess/SlideoutSubmitSuccess';
import SlideoutHeader from 'ps-components/Slideout/Header/SlideoutHeader';
import SlideoutBody from 'ps-components/Slideout/Body/SlideoutBody';
import SlideoutFooter from 'ps-components/Slideout/Footer/SlideoutFooter';
import ProgressStatusOverlay from 'ps-components/ProgressStatus/ProgressStatusOverlay';
import StructureBulkCopyBody from './StructureBulkCopyBody';
import { copyBulkModels, resetBulkCopyReducer } from './StructureBulkCopyActions';
import { getTargetElements } from './../StructureModelingActions';
import './StructureBulkCopySlideout.scss';
import { showSlideout, hideSlideout } from 'ps-components/Slideout/SlideoutReducer';
import { SLIDEOUTS } from '../../Slideout/Slideouts';
import { Button } from '../../../ps-aspect-design';
import { CONTACT_SUPPORT_MESSAGE } from '../../../lib/constants';

const buildRangeOptions = ['Keep Current Ranges', 'Midpoint', 'Market'];

const roundOptions = [
  {
    value: null,
    label: '-'
  },
  {
    value: 10,
    label: '10'
  },
  {
    value: 100,
    label: '100'
  },
  {
    value: 1000,
    label: '1000'
  }
];

StructureBulkCopySlideout.propTypes = {
  slideout: object,
  modelIds: array,
  hideSlideout: func,
  showSlideout: func,
  copyBulkModels: func,
  gridStore: object,
  bulkCopyLoading: bool,
  bulkCopyFail: bool,
  bulkCopySuccess: bool,
  resetBulkCopyReducer: func,
  groups: array,
  targetElements: array,
  getTargetElements: func,
  launchDarklyFeatureFlags: object,
  mainGroupId: number,
  currentGroup: object,
  permissions: object
};

export function StructureBulkCopySlideout(props) {
  const {
    modelIds,
    hideSlideout,
    showSlideout,
    copyBulkModels,
    gridStore,
    bulkCopyLoading,
    bulkCopyFail,
    bulkCopySuccess,
    resetBulkCopyReducer,
    slideout,
    groups,
    targetElements,
    launchDarklyFeatureFlags,
    mainGroupId,
    currentGroup,
    permissions
  } = props;

  // General
  const [showError, setShowError] = useState(false);

  // Models info card
  const [copyName, setCopyName] = useState('');
  const [namePosition, setNamePosition] = useState('before');
  const [scopeValue, setScopeValue] = useState('personal');
  const [description, setDescription] = useState('');
  const [allGroups, setAllGroups] = useState(false);
  const [groupList, setGroupList] = useState(mainGroupId !== parseInt(currentGroup.value) ? [currentGroup] : []);

  // Model settings card
  const [buildRange, setBuildRange] = useState(buildRangeOptions[0]);
  const [weightAverage, setWeightAverage] = useState('Employee');
  const [midpointAverage, setMidpointAverage] = useState(null);
  const [rangeSpread, setRangeSpread] = useState(50);
  const [rounding, setRounding] = useState(roundOptions[0]);
  const [midpointAdjustment, setMidpointAdjustment] = useState(0);

  let outputText = `Output: ${copyName ? copyName : 'Copy of'} [model name]`;
  if (namePosition === 'after') outputText = `Output: [model name] ${copyName ? copyName : 'Copy'}`;

  const modelInfoProps = {
    name: copyName,
    setName: setCopyName,
    scopeValue,
    setScopeValue,
    namePosition,
    setNamePosition,
    description,
    setDescription,
    fillerText: namePosition === 'after' ? 'Copy' : 'Copy of ',
    outputText,
    labelText: 'model name',
    allGroups,
    setAllGroups,
    groupList,
    setGroupList,
    scrollIntoView: true
  };

  const modelSettingsProps = {
    buildRange,
    setBuildRange,
    weightAverage,
    setWeightAverage,
    midpointAverage,
    setMidpointAverage,
    rangeSpread,
    setRangeSpread,
    rounding,
    setRounding,
    midpointAdjustment,
    setMidpointAdjustment,
    targetElements,
    buildRangeOptions,
    roundOptions
  };

  function resetStates() {
    setCopyName('');
    setNamePosition('before');
    setScopeValue('personal');
    setDescription('');
    setShowError(false);
    setBuildRange(buildRangeOptions[0]);
    setWeightAverage('Employee');
    setMidpointAverage(null);
    setRangeSpread(50);
    setRounding(roundOptions[0]);
    setMidpointAdjustment(0);
    setAllGroups(false);
    setGroupList(mainGroupId !== parseInt(currentGroup.value) ? [currentGroup] : []);
  }

  const titleText = `Copy ${modelIds.length} selected Models`;

  useEffect(() => {
    if (modelIds.length === 0) hideSlideout();
    if (!slideout.open) {
      resetBulkCopyReducer(true);
      resetStates();
    } else {
      props.getTargetElements();
    }
  }, [slideout.open]);

  useEffect(() => {
    if (bulkCopySuccess) gridStore.load();
  }, [bulkCopySuccess]);

  useEffect(() => {
    if (bulkCopyFail) {
      resetBulkCopyReducer();
      setShowError(true);
    }
  }, [bulkCopyFail]);

  function hideError() {
    setShowError(false);
  }

  function bulkCopy() {
    let adjustments = {};
    switch (buildRange) {
      case 'Keep Current Ranges':
        adjustments = null;
        break;
      case 'Midpoint':
        if (midpointAdjustment) adjustments.midpoint = midpointAdjustment / 100;
        if (rangeSpread) adjustments.rangeSpread = rangeSpread / 100;
        if (rounding.value) adjustments.round = rounding.value;
        break;
      case 'Market':
        if (weightAverage) adjustments.weightAverage = weightAverage;
        if (midpointAverage.value)
          adjustments.midpointAverage = {
            fieldPk: midpointAverage.value.field_pk,
            formulaPk: midpointAverage.value.formula_pk
          };
        if (rangeSpread) adjustments.rangeSpread = rangeSpread / 100;
        if (rounding.value) adjustments.round = rounding.value;
        break;
    }
    copyBulkModels(
      modelIds,
      namePosition,
      copyName,
      description,
      scopeValue,
      adjustments,
      allGroups
        ? groups.filter(group => parseInt(group.id) !== mainGroupId).map(group => parseInt(group.id))
        : groupList
    );
  }

  if (bulkCopySuccess)
    return (
      <SlideoutSubmitSuccess
        actionFunction={hideSlideout}
        actionText="Done"
        submitSuccessDetails="Go back to the models page to see the copied models."
        submitSuccessTitle={`${modelIds.length} Models(s) have been copied!`}
        closeAction={resetBulkCopyReducer}
      />
    );

  let isApplyDisabled = false;
  if ((buildRange === 'Market' && !midpointAverage) || (scopeValue === 'group' && !allGroups && groupList.length === 0))
    isApplyDisabled = true;
  return (
    <React.Fragment>
      <SlideoutHeader title={titleText} doClose={hideSlideout} />
      <SlideoutBody>
        {bulkCopyLoading && (
          <ProgressStatusOverlay
            inProgress={true}
            primaryText={'Copying ' + modelIds.length + ' model(s)...'}
            submitSuccessDetails="This might take a moment"
          />
        )}
        {showError && (
          <SlideoutError className="struc-bulk-copy__error" doClose={hideError}>
            <h4>An error occured during the bulk copy. Please try again.</h4>
            <div className="struc-bulk-copy__error-instructions">
              {CONTACT_SUPPORT_MESSAGE}
            </div>
          </SlideoutError>
        )}
        <StructureBulkCopyBody
          slideout={slideout}
          groups={groups.filter(group => parseInt(group.id) !== mainGroupId)}
          modelInfoProps={modelInfoProps}
          modelSettingsProps={modelSettingsProps}
          permissions={permissions}
        />
      </SlideoutBody>
      <SlideoutFooter footerClassName="struc-bulk-copy__footer" isBoxShadowTheme={true}>
        <Button variant="secondary" size="default" onClick={hideSlideout}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="default"
          onClick={bulkCopy}
          className="structure-bulk-copy__copy-button"
          disabled={isApplyDisabled}
        >
          Copy Models
        </Button>
      </SlideoutFooter>
    </React.Fragment>
  );
}

const mapStateToProps = ({ structureModeling, groups, session }) => {
  const { structureBulkCopy, sharedData } = structureModeling;
  const { userRights, group, organization } = session;
  let groupPermission = true,
    sitePermission = true,
    personalPermission = true;

  if (userRights.super_admin || userRights.site_admin) {
    groupPermission = true;
    sitePermission = true;
  } else if (userRights.group_admin) {
    groupPermission = true;
    sitePermission = userRights.allow_group_admins_publish_to_site;
  } else if (userRights.mp_write_p && userRights.mp_read_p) {
    groupPermission = userRights?.allow_regular_users_publish_to_group;
    sitePermission = userRights?.allow_regular_users_publish_to_site;
  } else if (userRights.mp_read_p) {
    groupPermission = false;
    sitePermission = false;
    personalPermission = false;
  }

  const mainGroupId = parseInt(organization?.defaultGroup?.id);

  if (groups.length === 1 && mainGroupId === parseInt(groups[0]?.id)) groupPermission = false;

  return {
    modelIds: structureBulkCopy.modelIds,
    gridStore: structureBulkCopy.gridStore,
    bulkCopyLoading: structureBulkCopy.bulkCopyLoading,
    bulkCopyFail: structureBulkCopy.bulkCopyFail,
    bulkCopySuccess: structureBulkCopy.bulkCopySuccess,
    targetElements: sharedData.targetElements,
    groups,
    currentGroup: { label: group.name, value: group.id },
    mainGroupId,

    permissions: {
      group: groupPermission,
      site: sitePermission,
      personal: personalPermission
    }
  };
};

const actions = {
  showSlideout,
  hideSlideout,
  copyBulkModels,
  resetBulkCopyReducer,
  getTargetElements
};

export default connect(mapStateToProps, actions)(withRouter(StructureBulkCopySlideout));
